import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Division, LineItemForPrediction, PredictedLineItemWithDivision } from './glossary.type';
import { predictDivisions } from './glossary.api';

const useAutoDivisionMapper = (divisions: Division[]) => {
  const [isAutoAssignDialogOpen, setIsAutoAssignDialogOpen] = useState(false);
  const [lineItemsToAutoAssign, setLineItemsToAutoAssign] = useState<PredictedLineItemWithDivision[]>([]);
  const [predictingDivisions, setPredictingDivisions] = useState(false);

  const openAutoAssignDialog = React.useCallback(
    async (lineItemsToAutoAssign: LineItemForPrediction[]) => {
      if (lineItemsToAutoAssign.length === 0) {
        toast.error('No line items to auto-assign');
        return;
      }

      try {
        setPredictingDivisions(true);

        // Step 1: Separate assigned and unassigned
        const unassignedItems = lineItemsToAutoAssign.filter(item => !item.division || item.division === null);
        const preAssignedItems = lineItemsToAutoAssign.filter(item => item.division !== null && item.division !== undefined);

        // Step 2: Predict only for unassigned
        const results = await predictDivisions(unassignedItems);

        // Step 3: Map predicted with additional info
        const predictedWithDivisions: PredictedLineItemWithDivision[] = results.map(li => {
          const division = divisions.find(d => d.name === li.division);
          const originalLineItem = unassignedItems.find(original => original.id === li.id);
          return {
            ...li,
            division: division ?? null,
            revisedBudget: originalLineItem?.revisedBudget,
            preAssignedDivision: false,
          };
        });

        // Step 4: Map already-assigned ones
        const assignedWithDivisions: PredictedLineItemWithDivision[] = preAssignedItems.map(li => {
          const division = divisions.find(d => d.name === li.division);
          return {
            ...li,
            division: division ?? null,
            revisedBudget: li.revisedBudget,
            preAssignedDivision: true,
          };
        });

        // Step 5: Combine while maintaining original order
        const lineItemsWithDivisions: PredictedLineItemWithDivision[] = lineItemsToAutoAssign.map(originalItem => {
          // First try to find in assigned items
          const assignedItem = assignedWithDivisions.find(item => item.id === originalItem.id);
          if (assignedItem) return assignedItem;

          // Then try to find in predicted items
          const predictedItem = predictedWithDivisions.find(item => item.id === originalItem.id);
          if (predictedItem) return predictedItem;

          return {
            id: originalItem.id,
            description: originalItem.description,
            division: null,
            revisedBudget: originalItem.revisedBudget,
            preAssignedDivision: false,
          };
        });

        setLineItemsToAutoAssign(lineItemsWithDivisions);
        setIsAutoAssignDialogOpen(true);
      } catch (error) {
        console.error('Error predicting divisions:', error);
        toast.error('Error while predicting divisions! Please try again later...');
        setIsAutoAssignDialogOpen(false);
      } finally {
        setPredictingDivisions(false);
      }
    },
    [divisions],
  );

  return { openAutoAssignDialog, isAutoAssignDialogOpen, lineItemsToAutoAssign, predictingDivisions, setIsAutoAssignDialogOpen };
};

export default useAutoDivisionMapper;
